
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { User } from '@supabase/supabase-js';
import { Poll } from '@/types/group';

export const usePollActions = (poll: Poll, onPollDeleted?: (pollId: string) => void) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);

  const handleClosePoll = async () => {
    try {
      const { error } = await supabase
        .from('polls')
        .update({ closed: true })
        .eq('id', poll.id);
      
      if (error) throw error;
      
      toast.success('Poll has been closed');
    } catch (error) {
      console.error('Error closing poll:', error);
      toast.error('Failed to close poll');
    }
  };

  const handleDeletePoll = async (user: User | null) => {
    if (!user) {
      toast.error('You need to be logged in to delete polls');
      return;
    }
    
    if (confirm('Are you sure you want to delete this poll? This action cannot be undone.')) {
      setIsDeleting(true);
      
      try {
        // First delete all votes
        const optionIds = poll.options?.map(opt => opt.id) || [];
        const { error: votesError } = await supabase
          .from('poll_votes')
          .delete()
          .in('poll_option_id', optionIds);
          
        if (votesError) {
          console.error('Error deleting votes:', votesError);
          throw votesError;
        }
        
        // Then delete the options
        const { error: optionsError } = await supabase
          .from('poll_options')
          .delete()
          .eq('poll_id', poll.id);
          
        if (optionsError) {
          console.error('Error deleting options:', optionsError);
          throw optionsError;
        }
        
        // Finally delete the poll
        const { error: pollError } = await supabase
          .from('polls')
          .delete()
          .eq('id', poll.id);
          
        if (pollError) {
          console.error('Error deleting poll:', pollError);
          throw pollError;
        }
        
        setIsDeleted(true);
        onPollDeleted?.(poll.id);
        toast.success('Poll deleted successfully');
      } catch (error) {
        console.error('Error deleting poll:', error);
        toast.error('Failed to delete poll');
      } finally {
        setIsDeleting(false);
      }
    }
  };

  return {
    isDeleting,
    isDeleted,
    handleClosePoll,
    handleDeletePoll
  };
};
